import React from "react";
import {
  HiX,
  HiInformationCircle,
  HiExclamationCircle,
  HiExclamation,
} from "react-icons/hi";
import { ReadMore } from "./ReadMore.tsx";

type BannerVariant = "error" | "info" | "warning";

interface BannerProps {
  variant: BannerVariant;
  title: string;
  body?: string;
  visible: boolean;
  onClose?: () => void;
  className?: string;
}

const Banner: React.FC<BannerProps> = ({
  variant,
  title,
  body,
  visible,
  onClose,
  className,
}) => {
  if (!visible) {
    return null;
  }

  const variantStyles = {
    error: {
      container: "bg-red-50 border-red-200 text-red-800",
      icon: "text-red-500",
      IconComponent: HiExclamationCircle,
    },
    warning: {
      container: "bg-yellow-50 border-yellow-200 text-yellow-800",
      icon: "text-yellow-500",
      IconComponent: HiExclamation,
    },
    info: {
      container: "bg-blue-50 border-blue-200 text-blue-800",
      icon: "text-blue-500",
      IconComponent: HiInformationCircle,
    },
  };

  const styles = variantStyles[variant];
  const IconComponent = styles.IconComponent;

  return (
    <div
      className={`border rounded-lg p-4 ${styles.container} ${className ?? ""}`}
      role="alert"
    >
      <div className="flex items-start gap-3">
        <IconComponent
          className={`${styles.icon} flex-shrink-0 mt-0.5`}
          size={20}
        />
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-sm">{title}</h3>
          {body && (
            <p className="text-sm mt-1">
              <ReadMore id="banner-text" text={body} />
            </p>
          )}
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="flex-shrink-0 hover:opacity-70 transition-opacity"
            aria-label="Close"
          >
            <HiX size={18} />
          </button>
        )}
      </div>
    </div>
  );
};

export { Banner };
export type { BannerVariant };
